import * as MD from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Hls, { ErrorData } from 'hls.js';
import * as React from 'react';
import { connect } from 'react-redux';
import { IApplicationState } from '../Store';
import { I18N } from '../Store/I18n/Types';
import { ILayoutState } from '../Store/Layout/Types';
import { IUserState } from '../Store/User/Types';
import { Logger } from '../Utils/Logger';

const styles = MD.createStyles({});

interface IState {
  currentHls: Hls;
  currentXhr: XMLHttpRequest | null;
  hlsErrorData: ErrorData | null;
  hlsUp: boolean;
  img: HTMLImageElement | null;
  imgError: boolean;
  imgErrorOpen: boolean;
  now: Date;
  playerAudio: HTMLAudioElement | null;
  playerVideo: HTMLVideoElement | null;
}

interface IPropsFromState {
  i18n: I18N;
  user: IUserState;
  layout: ILayoutState;
}

interface IProps {
  isVideo: boolean;
  autoplay: boolean;
  url: string;
}

interface IPropsFromDispatch {}

type AllProps = MD.WithStyles<typeof styles> &
  IPropsFromState &
  IPropsFromDispatch &
  IProps;

export class PlayerComponent extends React.PureComponent<AllProps, IState> {
  private containerRef: React.RefObject<HTMLDivElement>;
  public constructor(props: AllProps) {
    super(props);
    this.state = {
      currentHls: new Hls(),
      currentXhr: null,
      hlsErrorData: null,
      hlsUp: false,
      img: null,
      imgError: false,
      imgErrorOpen: true,
      now: new Date(),
      playerAudio: null,
      playerVideo: null,
    };
    this.containerRef = React.createRef<HTMLDivElement>();
  }

  private calculateWidthAndHeight(): { width: number; height: number } {
    const container = this.containerRef.current;
    if (container) {
      const containerWidth = container.clientWidth;
      // Calculate the nearest lower multiple of 16
      const adjustedWidth = Math.floor(containerWidth / 16) * 16;
      // Calculate the height based on the 16:9 ratio
      const height = (adjustedWidth * 9) / 16;
      return { width: adjustedWidth, height };
    }
    return { width: 0, height: 0 };
  }

  public componentDidUpdate(): void {
    const video = this.props.isVideo
      ? this.state.playerVideo
      : this.state.playerAudio;
    if (!this.state.hlsUp) {
      if (video !== null) {
        const hls = new Hls({
          manifestLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: Infinity,
              maxLoadTimeMs: 20000,
              timeoutRetry: {
                maxNumRetry: 0,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
              },
              errorRetry: {
                maxNumRetry: 1,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
              },
            },
          },
          playlistLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 10000,
              maxLoadTimeMs: 20000,
              timeoutRetry: {
                maxNumRetry: 0,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
              },
              errorRetry: {
                maxNumRetry: 0,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
              },
            },
          },
          fragLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 10000,
              maxLoadTimeMs: 120000,
              timeoutRetry: {
                maxNumRetry: 4,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
              },
              errorRetry: {
                maxNumRetry: 6,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
              },
            },
          },
          keyLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 8000,
              maxLoadTimeMs: 20000,
              timeoutRetry: {
                maxNumRetry: 1,
                retryDelayMs: 1000,
                maxRetryDelayMs: 20000,
                backoff: 'linear',
              },
              errorRetry: {
                maxNumRetry: 8,
                retryDelayMs: 1000,
                maxRetryDelayMs: 20000,
                backoff: 'linear',
              },
            },
          },
          certLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 8000,
              maxLoadTimeMs: 20000,
              timeoutRetry: null,
              errorRetry: null,
            },
          },
          steeringManifestLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 10000,
              maxLoadTimeMs: 20000,
              timeoutRetry: {
                maxNumRetry: 0,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
              },
              errorRetry: {
                maxNumRetry: 1,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
              },
            },
          },
          xhrSetup: (xhr: XMLHttpRequest) => {
            xhr.setRequestHeader(
              'authorization',
              `Bearer ${this.props.user.token}`
            );
            xhr.setRequestHeader('x-yacast-streamer-client', 'secure-player');
            this.setState({ currentXhr: xhr });
          },
        });
        hls.loadSource(this.props.url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (event, data) => {
          Logger.warn(event, data);
          this.setState({ hlsErrorData: data });
          if (this.state.currentXhr !== null) this.state.currentXhr.abort();
        });
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.setState({ hlsErrorData: null });
        });
        hls.config.debug = true;
        this.setState({ currentHls: hls, hlsUp: true });
      }
    }
  }

  public componentDidMount(): void {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }

  public componentWillUnmount(): void {
    window.removeEventListener('resize', this.handleResize);
    this.state.currentHls.destroy();
  }

  private handleResize = () => {
    this.forceUpdate(); // Force update to recalculate dimensions
  };

  static getDerivedStateFromProps(props: AllProps, state: IState) {
    return state;
  }

  public render() {
    const { width, height } = this.calculateWidthAndHeight();

    return (
      <div ref={this.containerRef} style={{ width: '100%' }}>
        <MD.Grid
          item
          xs={12}
          style={
            this.props.isVideo
              ? {
                  width: `${width}px`,
                  margin: '0 auto',
                  position: 'relative',
                  height: `${height}px`,
                }
              : {
                  width: `${width}px`,
                  margin: '0 auto',
                  position: 'relative',
                  height: `${54}px`,
                }
          }
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          >
            {this.props.isVideo ? (
              <video
                ref={(player) => {
                  this.setState({ playerVideo: player });
                }}
                style={{ width: '100%', height: '100%' }}
                autoPlay={this.props.autoplay}
                controls
              />
            ) : (
              <audio
                style={{ width: '100%', marginBottom: '-5px' }}
                ref={(player) => {
                  this.setState({ playerAudio: player });
                }}
                autoPlay={this.props.autoplay}
                controls
              />
            )}
          </div>
        </MD.Grid>
      </div>
    );
  }
}

const mapChunksStateToProps = ({ user, i18n, layout }: IApplicationState) => ({
  i18n: i18n.i18n,
  user,
  layout,
});

export const PlayerMaster = connect(mapChunksStateToProps)(
  MD.withTheme(MD.withStyles(styles)(PlayerComponent))
);
